import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import Navbar from "../components/Navbar";
import { useContext, useEffect, useState } from "react";
import { TeacherTable } from "../components/TeacherTable";
import NotificationContext from "../context/NotificationContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useMutationLock } from "../hooks/useMutationLock";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import * as teachersApi from "../api/teachers";

function AdminTeachers() {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_teachers");
  const { hasPermission: canEdit } = useViewPermission("edit_teachers");
  const { busy, run } = useMutationLock();

  const [teachers, setTeachers] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const limit = DEFAULT_PAGE_SIZE;
  const [teacher, setTeacher] = useState({
    name: "",
    phoneNumber: "",
    picture: "",
    caption: "",
    description: "",
    commission: 0,
  });
  const [file, setFile] = useState(null);

  const [showform, setshowform] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTeacher({ ...teacher, [name]: value });
  };

  const [loading, setLoading] = useState(true);

  const fetchTeachers = async (pageArg = page) => {
    try {
      const response = await teachersApi.list({ page: pageArg, limit });
      const normalized = normalizeListResponse(response.data, pageArg, limit);
      setTeachers(normalized.data);
      setPage(normalized.page);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages);
    } catch (err) {
      console.error("Error fetching teachers:", err);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchTeachers(newPage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    await run(async () => {
      try {
        if (!file) {
          notificationCtx.show({
            message: "Please upload a picture for the teacher.",
          });
          return;
        }
        const formData = new FormData();
        formData.append("picture", file);
        formData.append("name", teacher.name);
        formData.append("phoneNumber", teacher.phoneNumber);
        formData.append("caption", teacher.caption);
        formData.append("description", teacher.description);
        formData.append("commission", teacher.commission);
        await teachersApi.create(formData);
        setPage(1);
        await fetchTeachers(1);
        setTeacher({
          name: "",
          phoneNumber: "",
          picture: "",
          caption: "",
          description: "",
          commission: 0,
        });
        setFile(null);
        setshowform(false);
        notificationCtx.show({
          message: "Teacher added successfully!",
          stat: "success",
        });
      } catch (error) {
        console.error("Error adding teacher:", error);
        notificationCtx.show({
          message: "Failed to add teacher.",
          stat: "error",
        });
      }
    });
  };

  useEffect(() => {
    if (!hasPermission) return;
    const loadTeachers = async () => {
      try {
        await fetchTeachers(1);
      } finally {
        setLoading(false);
      }
    };
    loadTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  const handleEdit = (teacherId, field, value) => {
    if (!canEdit) return;
    setTeachers((prevTeachers) =>
      prevTeachers.map((teacher) =>
        teacher.id === teacherId ? { ...teacher, [field]: value } : teacher,
      ),
    );
  };

  const handleSave = async (id, field, value) => {
    if (!canEdit) return;
    await run(async () => {
      try {
        await teachersApi.update(id, { field, value });
        notificationCtx.show({
          message: "Teacher field updated successfully!",
          stat: "success",
        });
      } catch (error) {
        console.error("Error updating teacher field:", error);
        notificationCtx.show({
          message: "Failed to update teacher field.",
          stat: "error",
        });
      }
    });
  };

  const columnKeys = [
    "id",
    "name",
    "phoneNumber",
    "picture",
    "caption",
    "description",
  ];

  const handleDeleteTeacher = (teacherId) => {
    if (!canEdit) return;

    const confirmDeleteTeacher = async () => {
      try {
        await teachersApi.remove(teacherId);

        const nextPage =
          teachers.length <= 1 && page > 1 ? page - 1 : page;
        setPage(nextPage);
        await fetchTeachers(nextPage);
        notificationCtx.show({
          message: "Teacher deleted successfully",
          stat: "success",
        });
      } catch (error) {
        console.error("Delete failed:", error);
        notificationCtx.show({
          message: "Failed to delete teacher",
          stat: "error",
        });
      }
    };

    notificationCtx.show({
      message: "Are you sure you want to delete this teacher?",
      stat: "info",
      onConfirm: confirmDeleteTeacher,
    });
  };

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div className="mb-5">
      <Navbar />
      {isAuthenticated && hasPermission && (
        <div
          className="d-flex flex-column"
          style={{
            height: "109vh",
            minHeight: 0,
            padding: "10px 50px 0",
            overflow: "hidden",
          }}
        >
          {/* ================= TOP HEADER ================= */}
          <div
            style={{
              backgroundColor: "#d5d1fd",
              borderRadius: "20px",
              padding: "26px 30px",
              flexShrink: 0,
              zIndex: 10,
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1
                  className="mb-1"
                  style={{
                    // fontSize: "28px",
                    fontWeight: "700",
                    color: "#302d45",
                  }}
                >
                  ALL TEACHERS
                </h1>

                <small
                  style={{
                    color: "#625d75",
                  }}
                >
                  Manage teachers, profiles, and teacher's share information
                </small>
              </div>

              {canEdit && (
                <button
                  className="btn"
                  onClick={() => setshowform(true)}
                  style={{
                    height: "38px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#fff",
                    color: "#403b55",
                    border: "none",
                    padding: "0 15px",
                    borderRadius: "8px",
                    fontWeight: "600",
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                  }}
                >
                  + Add Teacher
                </button>
              )}
            </div>
          </div>

          <br />

          {/* ================= TABLE ================= */}

          {teachers.length === 0 ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{
                padding: "60px 20px",
                color: "#77718b",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#403b55",
                }}
              >
                No teachers available
              </div>

              <small style={{ marginTop: "5px" }}>
                There are currently no teachers to display.
              </small>
            </div>
          ) : (
            <TeacherTable
              data={teachers}
              columnKeys={columnKeys}
              handleEdit={handleEdit}
              handleSave={handleSave}
              onDelete={handleDeleteTeacher}
              onRefresh={() => fetchTeachers(page)}
              onPictureUpdated={(id, pictureUrl) => {
                setTeachers((prev) =>
                  prev.map((row) =>
                    String(row.id) === String(id)
                      ? { ...row, picture: pictureUrl }
                      : row,
                  ),
                );
              }}
              canEdit={canEdit}
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}

          {/* ================= ADD TEACHER MODAL ================= */}

          {canEdit && showform && (
            <div
              className="modal show"
              style={{
                display: "block",
                backgroundColor: "rgba(48,45,69,0.45)",
              }}
              tabIndex="-1"
            >
              <div
                className="modal-dialog modal-dialog-centered modal-lg"
                style={{
                  maxWidth: "650px",
                }}
              >
                <div
                  className="modal-content"
                  style={{
                    border: "none",
                    borderRadius: "18px",
                    overflow: "hidden",
                    boxShadow: "0 10px 35px rgba(48,45,69,0.18)",
                  }}
                >
                  {/* ================= MODAL HEADER ================= */}

                  <div
                    style={{
                      padding: "22px 24px",
                      backgroundColor: "#d5d1fd",
                      borderBottom: "1px solid rgba(80,70,110,0.08)",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4
                          className="mb-1"
                          style={{
                            fontWeight: "700",
                            color: "#302d45",
                          }}
                        >
                          Add Teacher
                        </h4>

                        {/* <small
                          style={{
                            color: "#625d75",
                          }}
                        >
                          Add a new teacher to the system
                        </small> */}
                      </div>

                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setshowform(false)}
                        aria-label="Close"
                      />
                    </div>
                  </div>

                  {/* ================= MODAL BODY ================= */}

                  <div
                    style={{
                      padding: "24px",
                      backgroundColor: "#fff",
                      maxHeight: "90vh",
                      overflowY: "auto",
                    }}
                  >
                    <form onSubmit={handleSubmit}>
                      {/* Name */}

                      <div className="mb-3">
                        <label
                          htmlFor="teacherName"
                          className="form-label"
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#403b55",
                          }}
                        >
                          Name
                        </label>

                        <input
                          id="teacherName"
                          type="text"
                          name="name"
                          value={teacher.name}
                          onChange={handleChange}
                          required
                          className="form-control"
                          placeholder="Enter teacher name"
                          style={{
                            border: "1px solid #e5e1ef",
                            borderRadius: "9px",
                            padding: "10px 13px",
                            color: "#403b55",
                            backgroundColor: "#fff",
                          }}
                        />
                      </div>

                      {/* Phone Number */}

                      <div className="mb-3">
                        <label
                          htmlFor="teacherPhone"
                          className="form-label"
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#403b55",
                          }}
                        >
                          Phone Number
                        </label>

                        <input
                          id="teacherPhone"
                          type="text"
                          name="phoneNumber"
                          value={teacher.phoneNumber}
                          onChange={handleChange}
                          required
                          className="form-control"
                          placeholder="Enter phone number"
                          style={{
                            border: "1px solid #e5e1ef",
                            borderRadius: "9px",
                            padding: "10px 13px",
                            color: "#403b55",
                          }}
                        />
                      </div>

                      {/* Picture */}

                      <div className="mb-3">
                        <label
                          htmlFor="teacherPicture"
                          className="form-label"
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#403b55",
                          }}
                        >
                          Teacher's Picture
                        </label>

                        <input
                          id="teacherPicture"
                          type="file"
                          accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                          className="form-control"
                          onChange={(e) => setFile(e.target.files[0])}
                          style={{
                            border: "1px solid #e5e1ef",
                            borderRadius: "9px",
                            padding: "8px 10px",
                            backgroundColor: "#fff",
                          }}
                        />

                        <small
                          style={{
                            display: "block",
                            marginTop: "6px",
                            color: "#8a8598",
                            fontSize: "12px",
                          }}
                        >
                          Upload a clear profile picture.
                        </small>
                      </div>

                      {/* Caption */}

                      <div className="mb-3">
                        <label
                          htmlFor="teacherCaption"
                          className="form-label"
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#403b55",
                          }}
                        >
                          Caption
                        </label>

                        <input
                          id="teacherCaption"
                          type="text"
                          name="caption"
                          value={teacher.caption}
                          onChange={handleChange}
                          required
                          className="form-control"
                          placeholder="Enter teacher caption"
                          style={{
                            border: "1px solid #e5e1ef",
                            borderRadius: "9px",
                            padding: "10px 13px",
                            color: "#403b55",
                          }}
                        />
                      </div>

                      {/* Description */}

                      <div className="mb-3">
                        <label
                          htmlFor="teacherDescription"
                          className="form-label"
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#403b55",
                          }}
                        >
                          Description
                        </label>

                        <textarea
                          id="teacherDescription"
                          name="description"
                          value={teacher.description}
                          onChange={handleChange}
                          required
                          rows={3}
                          className="form-control"
                          placeholder="Enter teacher description"
                          style={{
                            border: "1px solid #e5e1ef",
                            borderRadius: "9px",
                            padding: "10px 13px",
                            color: "#403b55",
                            resize: "vertical",
                          }}
                        />
                      </div>

                      {/* Commission */}

                      <div className="mb-4">
                        <label
                          htmlFor="teacherCommission"
                          className="form-label"
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#403b55",
                          }}
                        >
                          Commission (Zainematics Share)
                        </label>

                        <input
                          id="teacherCommission"
                          type="number"
                          name="commission"
                          value={teacher.commission}
                          onChange={handleChange}
                          required
                          className="form-control"
                          placeholder="Enter commission"
                          style={{
                            border: "1px solid #e5e1ef",
                            borderRadius: "9px",
                            padding: "10px 13px",
                            color: "#403b55",
                          }}
                        />
                      </div>

                      {/* Actions */}

                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn"
                          onClick={() => setshowform(false)}
                          style={{
                            backgroundColor: "#f3f1f7",
                            border: "none",
                            color: "#625d75",
                            fontWeight: "600",
                            padding: "9px 18px",
                            borderRadius: "8px",
                          }}
                        >
                          Cancel
                        </button>

                        <button
                          type="submit"
                          className="btn"
                          disabled={busy}
                          style={{
                            backgroundColor: "#bff1db",
                            border: "none",
                            color: "#355c4a",
                            fontWeight: "600",
                            padding: "9px 20px",
                            borderRadius: "8px",
                          }}
                        >
                          {busy ? "Working..." : "Add Teacher"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminTeachers;
